import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Bell, Loader2, CheckCheck } from 'lucide-react';
import NotificationItem from './NotificationItem';
import useNotifications from '../hooks/useNotifications';

const NotificationPanelHeader = ({ unreadCount, loading, onMarkAllRead }) => (
  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
    <h4 className="font-semibold text-sm text-gray-900">Notifications</h4>
    {unreadCount > 0 && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onMarkAllRead}
        disabled={loading}
        className="text-xs text-tp-primary hover:text-tp-primary"
        data-testid="mark-all-read-btn"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3 mr-1" />}
        Tout lire
      </Button>
    )}
  </div>
);

const NotificationEmptyState = () => (
  <div className="px-4 py-8 text-center">
    <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
    <p className="text-sm text-gray-400">Aucune notification</p>
  </div>
);

const InAppNotifications = ({ className = '' }) => {
  const [showPanel, setShowPanel] = useState(false);
  const panelRef = useRef(null);

  const {
    notifications,
    unreadCount,
    loading,
    markAllRead,
    markOneReadAndNavigate,
  } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClick = async (notif) => {
    await markOneReadAndNavigate(notif);
    setShowPanel(false);
  };

  return (
    <div className={`relative ${className}`} ref={panelRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowPanel(!showPanel)}
        className="relative text-white/70 hover:text-white"
        data-testid="notification-bell-inapp"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-[16px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {showPanel && (
        <div
          className="fixed inset-x-3 top-14 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 w-auto sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <NotificationPanelHeader
            unreadCount={unreadCount}
            loading={loading}
            onMarkAllRead={markAllRead}
          />

          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notif, idx) => (
                <NotificationItem
                  key={notif.id || notif._id || idx}
                  notif={notif}
                  index={idx}
                  onClick={handleClick}
                />
              ))
            ) : (
              <NotificationEmptyState />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InAppNotifications;
